import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';
import axios, { STRAPI_URL } from '@propertypal/shared/src/services/axios.strapi';
import purifyHtml from '@propertypal/shared/src/services/dompurify';
import { FaqFuzzySearch, FaqSearchItem } from '@propertypal/shared/src/types/strapi/fuzzy';
import highlightTokens from '@propertypal/shared/src/utils/faq/highlightTokens';
import platform from '@propertypal/shared/src/utils/platform';
import puralise from '@propertypal/shared/src/utils/puralise';
import { convert } from 'html-to-text';
import React, { useEffect, useState } from 'react';
import Loader from 'react-spinners/PulseLoader';
import { useTheme } from 'styled-components';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { MidHeading, Text } from '../typography';
import FaqBreadcrumbs from './FaqBreadcrumbs';
import { FaqMainContent, FaqMainContentWrapper } from './FaqContentContainer.style';
import {
  FaqSearchContent,
  FaqSearchLink,
  FaqSearchNoResults,
  FaqSearchResultsContainer,
  LoadingBox,
  TotalResults,
} from './FaqSearchResults.style';

// Extract the first 200 characters from the HTML string
const truncatedHTML = (content: string | null, tokens: string[]) => {
  const txt = convert(content ?? '', {
    selectors: [
      { selector: 'img', format: 'skip' },
      { selector: 'a', options: { ignoreHref: true } },
    ],
  });
  if (txt && txt !== '...') {
    const truncated = txt.replace(`\u00A0`, '').slice(0, 200);
    return `<span>${highlightTokens(truncated, tokens)}${txt.length > truncated.length ? '...' : ''}</span>`;
  }
  return '';
};

interface FaqSearchResultsProps {
  searchQuery: string;
  onClearQuery: () => void;
  agent?: boolean;
}

const FaqSearchResults: React.FC<FaqSearchResultsProps> = ({ searchQuery, onClearQuery, agent }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [faqs, setFaqs] = useState<FaqSearchItem[]>([]);
  const [tokens, setTokens] = useState<string[]>([]);
  const [totalResults, setTotalResults] = useState<number>(0);

  const theme = useTheme();

  const faqUrl = `/faq${agent ? '/agent-faq' : ''}`;

  const fetchResult = async () => {
    setLoading(true);

    try {
      const platformFilter: Record<string, string> = {
        'filters[faqs][$or][0][platform][$eq]': platform.title === 'Propertynews' ? 'Propertynews' : 'PropertyPal',
        'filters[faqs][$or][1][platform][$eq]': 'Both',
        'filters[faqs][$or][3]faqSection][platform][$eq]':
          platform.title === 'Propertynews' ? 'Propertynews' : 'PropertyPal',
        'filters[faqs][$or][4][faqSection][platform][$eq]': 'Both',
      };

      if (platform.title === 'PropertyPal') {
        platformFilter['filters[faqs][$or][2][platform][$null]'] = 'true';
        platformFilter['filters[faqs][$or][5][faqSection][platform][$null]'] = 'true';
      }

      const result = await axios<FaqFuzzySearch>({
        method: 'GET',
        url: `${STRAPI_URL}/fuzzy-search/search`,
        params: {
          query: searchQuery,
          'filters[contentTypes]': 'faqs',
          'filters[faqs][publishedAt][$notNull]': 'true',
          'filters[faqs][subSection][faqSection][$eqi]': agent ? 'Agent FAQ' : 'Website User FAQ',
          ...platformFilter,
          'populate[faqs][subSection][populate][0]': 'true',
          'populate[faqs][tags][populate][1]': 'true',
          'pagination[faqs][withcount]': 'true',
        },
      });

      setFaqs(result.data?.faqs?.data);
      setTotalResults(result.data?.faqs?.meta.pagination?.total || 0);
      setError(false);
    } catch (err: any) {
      setError(true);
      setFaqs([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  // tokenise search query
  useEffect(() => {
    setTokens(searchQuery.split(/\s+/));
  }, [searchQuery]);

  // fetch faqs that match search query
  useEffect(() => {
    fetchResult();
  }, [JSON.stringify(tokens)]);

  return (
    <FaqMainContent data-testid="faq-search-results-container">
      <FaqMainContentWrapper>
        <FaqBreadcrumbs
          breadcrumbs={[
            { title: 'Home', url: '/' },
            { title: agent ? 'Agent FAQ' : 'FAQ Homepage', url: faqUrl },
            { title: `${searchQuery}${!searchQuery?.endsWith('?') ? '?' : ''}` },
          ]}
          onClearQuery={onClearQuery}
        />
        <FaqSearchResultsContainer>
          {loading && (
            <LoadingBox data-testid="faq-search-loading">
              <Loader color={theme.primary} size={20} speedMultiplier={0.6} margin={8} />
            </LoadingBox>
          )}
          {!loading && faqs.length > 0 && (
            <div data-testid="faq-search-results">
              <TotalResults fontSize={30} mb={55} data-testid="faq-total-results">
                {totalResults} {puralise('result', 'results', totalResults)} for &quot;{searchQuery}&quot;
              </TotalResults>
              {faqs.map((faq) => (
                <div key={faq.id} data-testid="faq-search-result">
                  <FaqSearchLink
                    prefetch={false}
                    href={`${faqUrl}/${faq.subSection?.[0]?.slug}/${faq.slug}`}
                    mb={10}
                    display="inline-block"
                    fontWeight="bold"
                    fontSize={18}
                    dangerouslySetInnerHTML={{
                      __html: purifyHtml(highlightTokens(faq.question, tokens)),
                    }}
                    data-testid="faq-search-result-link"
                  />
                  <FaqBreadcrumbs
                    breadcrumbs={[
                      { title: 'Home', url: '/' },
                      { title: agent ? 'Agent FAQ' : 'FAQ Homepage', url: faqUrl },
                      {
                        title: faq.subSection?.[0]?.subSection ?? 'FAQs',
                        url: `${faqUrl}/${faq.subSection?.[0]?.slug}`,
                      },
                      {
                        title: (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: purifyHtml(highlightTokens(faq.question, tokens)),
                            }}
                          />
                        ),
                      },
                    ]}
                    searchItem
                    onClearQuery={onClearQuery}
                  />
                  <FaqSearchContent html={truncatedHTML(faq.content, tokens)} />
                </div>
              ))}
            </div>
          )}
          {!loading && !faqs.length && !error && (
            <FaqSearchNoResults data-testid="faq-search-no-results">
              <FontAwesomeIcon icon={faSearch} size={40} />
              <MidHeading fontSize={40} mb={10} mt={10}>
                No Results
              </MidHeading>
              <Text fontSize={18}>Try changing your search criteria.</Text>
            </FaqSearchNoResults>
          )}
          {error && (
            <FaqSearchNoResults data-testid="faq-search-error">
              <MidHeading fontSize={40} mb={10} mt={10}>
                Error
              </MidHeading>
              <Text fontSize={18}>
                Oops, something has went wrong during your search, contact {platform.helpEmail}.
              </Text>
            </FaqSearchNoResults>
          )}
        </FaqSearchResultsContainer>
      </FaqMainContentWrapper>
    </FaqMainContent>
  );
};

export default FaqSearchResults;
